import { gql, useApolloClient } from "@apollo/client";
import { format as formatDate, fromUnixTime } from "date-fns";
import { BigNumber } from "ethers";
import { useForm } from "react-hook-form";

import {
  Alert,
  Button,
  Form,
  InfoIconTooltip,
  Link,
  MiniTable,
} from "@/components/design-system";
import {
  LEND_EAST_BACKSTOP_WALLET,
  STRATOS_BACKSTOP_WALLET,
} from "@/constants";
import { getContract } from "@/lib/contracts";
import { formatCrypto } from "@/lib/format";
import {
  ClaimPanelPoolTokenFieldsFragment,
  ClaimPanelLoanFieldsFragment,
  ClaimPanelVaultedPoolTokenFieldsFragment,
  UserEligibilityFieldsFragment,
} from "@/lib/graphql/generated";
import { canUserParticipateInPool, gfiToUsdc, sum } from "@/lib/pools";
import { openVerificationModal } from "@/lib/state/actions";
import { toastTransaction } from "@/lib/toast";
import { useWallet } from "@/lib/wallet";

import { CallPanel } from "./call/call-panel";

export const CLAIM_PANEL_POOL_TOKEN_FIELDS = gql`
  fragment ClaimPanelPoolTokenFields on PoolToken {
    id
    principalAmount
    principalRedeemable @client
    principalRedeemed
    interestRedeemable
    rewardsClaimable
    rewardsClaimed
    stakingRewardsClaimable
    stakingRewardsClaimed
    loan {
      id
    }
    ...CallPanelPoolTokenFields
  }
`;

export const CLAIM_PANEL_VAULTED_POOL_TOKEN_FIELDS = gql`
  ${CLAIM_PANEL_POOL_TOKEN_FIELDS}
  fragment ClaimPanelVaultedPoolTokenFields on VaultedPoolToken {
    id
    poolToken {
      ...ClaimPanelPoolTokenFields
    }
  }
`;

export const CLAIM_PANEL_LOAN_FIELDS = gql`
  fragment ClaimPanelLoanFields on Loan {
    __typename
    id
    allowedUidTypes
    delinquency @client
    termStartTime
    ... on CallableLoan {
      ...CallPanelCallableLoanFields
      loanPhase @client
    }
  }
`;

interface ClaimPanelProps {
  poolTokens: ClaimPanelPoolTokenFieldsFragment[];
  vaultedPoolTokens: ClaimPanelVaultedPoolTokenFieldsFragment[];
  fiatPerGfi: number;
  loan: ClaimPanelLoanFieldsFragment;
  claimableFromStratosBackstop?: CryptoAmount<"USDC">;
  claimableFromLendEastBackstop?: CryptoAmount<"USDC">;
  user: UserEligibilityFieldsFragment;
}

/**
 * This component is meant to be for claiming interest and GFI after a pool is locked.
 * If you're looking for the component that allows you to withdraw before a pool is locked, go to withdrawal-panel.tsx
 */
export function ClaimPanel({
  poolTokens,
  vaultedPoolTokens,
  fiatPerGfi,
  loan,
  claimableFromStratosBackstop = { token: "USDC", amount: BigNumber.from(0) },
  claimableFromLendEastBackstop = { token: "USDC", amount: BigNumber.from(0) },
  user,
}: ClaimPanelProps) {
  const combinedTokens = poolTokens.concat(
    vaultedPoolTokens.map((vpt) => vpt.poolToken)
  );

  const positionValue = {
    token: "USDC",
    amount: combinedTokens.reduce(
      (prev, current) =>
        prev
          .add(current.principalAmount)
          .sub(current.principalRedeemed)
          .add(current.interestRedeemable),
      BigNumber.from(0)
    ),
  } as const;

  const claimableUsdc = {
    token: "USDC",
    amount: sum("principalRedeemable", combinedTokens)
      .add(sum("interestRedeemable", combinedTokens))
      .add(claimableFromStratosBackstop.amount)
      .add(claimableFromLendEastBackstop.amount),
  } as const;

  const claimableGfi = {
    token: "GFI",
    amount: sum("rewardsClaimable", combinedTokens).add(
      sum("stakingRewardsClaimable", combinedTokens)
    ),
  } as const;
  const claimableGfiAsUsdc = gfiToUsdc(claimableGfi, fiatPerGfi);
  // Hack hot fix to allow for GIP-64 to be enabled. Refactoring this variable out entirely would be better.
  const hasUnreachableGfi = false;

  const rhfMethods = useForm();
  const { signer, account } = useWallet();
  const apolloClient = useApolloClient();

  const claim = async () => {
    if (!signer || !account) {
      throw new Error("Wallet not properly connected");
    }

    const withrawablePoolTokens = poolTokens.filter((pt) =>
      // TranchedPools throw an exception when trying to withdraw 0 amounts
      pt.principalRedeemable.add(pt.interestRedeemable).gt(0)
    );
    if (withrawablePoolTokens.length > 0) {
      const loanContract = await getContract({
        name:
          loan.__typename === "TranchedPool" ? "TranchedPool" : "CallableLoan",
        address: loan.id,
        signer,
      });
      const usdcTransaction = loanContract.withdrawMultiple(
        withrawablePoolTokens.map((pt) => pt.id),
        withrawablePoolTokens.map((pt) =>
          pt.principalRedeemable.add(pt.interestRedeemable)
        )
      );
      await toastTransaction({
        transaction: usdcTransaction,
        pendingPrompt: "Claiming USDC from your pool token",
      });

      if (loan.delinquency === "CURRENT" && !claimableGfi.amount.isZero()) {
        const backerRewardsContract = await getContract({
          name: "BackerRewards",
          signer,
        });
        const gfiTransaction = backerRewardsContract.withdrawMultiple(
          poolTokens.map((pt) => pt.id)
        );
        await toastTransaction({
          transaction: gfiTransaction,
          pendingPrompt: "Claiming GFI rewards from your pool tokens",
        });
      }
    }

    const withrawableVaultedPoolTokens = vaultedPoolTokens.filter((pt) =>
      // TranchedPools throw an exception when trying to withdraw 0 amounts
      pt.poolToken.principalRedeemable
        .add(pt.poolToken.interestRedeemable)
        .gt(0)
    );
    if (withrawableVaultedPoolTokens.length > 0) {
      const membershipOrchestrator = await getContract({
        name: "MembershipOrchestrator",
        signer,
      });
      const transaction = membershipOrchestrator.harvest(
        withrawableVaultedPoolTokens.map((vpt) => vpt.id)
      );
      await toastTransaction({
        transaction,
        pendingPrompt:
          "Claiming USDC and GFI rewards from your vaulted pool tokens",
      });
    }

    if (!claimableFromStratosBackstop.amount.isZero()) {
      const usdcContract = await getContract({ name: "USDC", signer });
      const transaction = usdcContract.transferFrom(
        STRATOS_BACKSTOP_WALLET,
        account,
        claimableFromStratosBackstop.amount
      );
      await toastTransaction({
        transaction,
        pendingPrompt: "Claiming USDC entitlement from backstop.",
      });
    }

    if (!claimableFromLendEastBackstop.amount.isZero()) {
      const usdcContract = await getContract({ name: "USDC", signer });
      const transaction = usdcContract.transferFrom(
        LEND_EAST_BACKSTOP_WALLET,
        account,
        claimableFromLendEastBackstop.amount
      );
      await toastTransaction({
        transaction,
        pendingPrompt: "Claiming USDC entitlement from backstop.",
      });
    }

    await apolloClient.refetchQueries({ include: "active" });
  };

  const isUserVerified = user?.isGoListed || !!user?.uidType;
  const canUserParticipate = !user
    ? false
    : canUserParticipateInPool(loan.allowedUidTypes, user);

  const claimDisabled =
    (claimableUsdc.amount.isZero() && claimableGfi.amount.isZero()) ||
    (claimableUsdc.amount.isZero() && hasUnreachableGfi) ||
    (vaultedPoolTokens.length > 0 && hasUnreachableGfi) ||
    (loan.__typename === "CallableLoan" &&
      loan.loanPhase === "DrawdownPeriod") ||
    !isUserVerified ||
    !canUserParticipate;

  return (
    <div>
      <div className="mb-6">
        <div className="mb-1 flex items-center justify-between gap-2">
          <div>Your current position value</div>
          <InfoIconTooltip content="The remaining principal on this position plus any accrued interest." />
        </div>
        <div className="text-3xl font-semibold">
          {formatCrypto(positionValue)}
        </div>
      </div>
      <div className="mb-3">
        <div className="mb-1 flex items-center justify-between gap-2">
          <div>
            Available to claim
            {!claimableFromStratosBackstop.amount.isZero() ||
            !claimableFromLendEastBackstop.amount.isZero()
              ? "*"
              : null}
          </div>
          <InfoIconTooltip content="The combined dollar value of claimable principal, interest, and GFI rewards on this position." />
        </div>
        <div className="text-3xl">
          {formatCrypto({
            token: "USDC",
            amount: claimableUsdc.amount.add(claimableGfiAsUsdc.amount),
          })}
        </div>
      </div>
      {/* eslint-disable react/jsx-key */}
      <MiniTable
        className="mb-4"
        colorScheme="mustard"
        rows={[
          [
            <div className="flex items-center justify-between gap-2">
              USDC
              <InfoIconTooltip
                size="xs"
                content="This includes your claimable principal and interest."
              />
            </div>,
            formatCrypto(claimableUsdc, {
              includeSymbol: false,
              includeToken: true,
            }),
            formatCrypto(claimableUsdc, {
              includeSymbol: true,
              includeToken: false,
            }),
          ],
          [
            <div className="flex items-center justify-between gap-2">
              GFI
              <InfoIconTooltip
                size="xs"
                content="Your GFI rewards for backing this pool."
              />
            </div>,
            formatCrypto(claimableGfi, {
              includeSymbol: false,
              includeToken: true,
            }),
            formatCrypto(claimableGfiAsUsdc, {
              includeSymbol: true,
              includeToken: false,
            }),
          ],
        ]}
      />
      {!claimableFromStratosBackstop.amount.isZero() ? (
        <div className="-mt-2 mb-4 text-xs">
          *The USDC amount includes both a) repayments from Stratos, and b) the
          backstop amount from Warbler Labs described in{" "}
          <Link
            href="https://gov.goldfinch.finance/t/secured-u-s-fintech-yield-pool-update/1923"
            openInNewTab
          >
            this governance update
          </Link>
          . When you click Claim, it will initiate both of these amounts as
          multiple separate transactions.
        </div>
      ) : !claimableFromLendEastBackstop.amount.isZero() ? (
        <div className="-mt-2 mb-4 text-xs">
          *The USDC amount includes the backstop amount from Warbler Labs
          described in{" "}
          <Link
            href="https://gov.goldfinch.finance/t/gip-67-new-proposal-for-using-gfi-to-help-with-principal-loss/2010"
            openInNewTab
          >
            this governance update
          </Link>
          . When you click Claim, it will initiate both of these amounts as
          multiple separate transactions.
        </div>
      ) : null}
      {!isUserVerified || !canUserParticipate ? (
        <>
          <Button
            className="block w-full"
            size="xl"
            colorScheme="mustard"
            onClick={openVerificationModal}
          >
            Verify my identity
          </Button>
          {!isUserVerified ? (
            <Alert type="warning" className="mt-4">
              You cannot claim from this pool because you do not have a UID.
            </Alert>
          ) : !canUserParticipate ? (
            <Alert type="warning" className="mt-4">
              You cannot claim from this pool because you do not have the
              appropriate UID for this pool.
            </Alert>
          ) : null}
        </>
      ) : (
        <>
          <Form rhfMethods={rhfMethods} onSubmit={claim}>
            <Button
              type="submit"
              className="w-full"
              size="xl"
              colorScheme="mustard"
              disabled={claimDisabled}
            >
              Claim
            </Button>
          </Form>
          {vaultedPoolTokens.length > 0 && hasUnreachableGfi ? (
            <Alert type="warning" className="mt-4">
              <div>
                <div>
                  Claiming is disabled because your pool token is vaulted and
                  this pool is late on repayment. If you wish to claim the USDC
                  from this late pool, you must first unvault your token.
                </div>
                <Link href="/membership" iconRight="ArrowSmRight">
                  Go to vault
                </Link>
              </div>
            </Alert>
          ) : hasUnreachableGfi ? (
            <Alert type="warning" className="mt-4">
              You cannot claim GFI rewards from this pool because it is late on
              repayment.
            </Alert>
          ) : loan.__typename === "CallableLoan" &&
            loan.loanPhase === "DrawdownPeriod" ? (
            <Alert type="warning" className="mt-4">
              You cannot claim during the drawdown period of a loan. Claiming
              will become available on{" "}
              {formatDate(
                fromUnixTime(loan.termStartTime.toNumber() + 432000), // The 432000 is just drawdownPeriodInSeconds from GoldfinchConfig. Could put this in the subgraph later
                "MMM d, yyyy"
              )}
              .
            </Alert>
          ) : null}
          {loan.__typename === "CallableLoan" ? (
            <CallPanel
              className="mt-8"
              callableLoan={loan}
              poolTokens={poolTokens}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
